import { Company, CompanyStage, Organization } from "./companyTypes";
import { PaginatedResponse } from "./globalTypes";
import { FormQuestionOption, FormQuestionType } from "./reportTypes";
import { User } from "./userTypes";

// responder
export enum ResponderGrade {
  CYCLE = "CYCLE",
  DIPLOMA = "DIPLOMA",
  ASSOCIATE = "ASSOCIATE",
  BACHELOR = "BACHELOR",
  MASTER = "MASTER",
  PHD = "PHD",
  POST_DOCTORAL = "POST_DOCTORAL",
  OTHER = "OTHER",
}

export interface Major {
  id: number;
  name: string;
  meta: { [key: string]: any };
}

export type MajorResponse = PaginatedResponse<Major>;

export interface Responder {
  id: number;
  name: string;
  email: string | null;
  degree: ResponderGrade;
  degreesOther: string;
  major: Major;
  majorId: number | null;
  majorOther: string;
  yearsOfExperience: number;
  position: string;
  expertise: string;
  nationalCode: string;
  mobile: string;
  createdAt: string;
  updatedAt: string;
  createdBy: Pick<User, "id" | "fullname" | "email" | "mobile">;
  updatedBy: Pick<User, "id" | "fullname" | "email" | "mobile">;
  avatar: string | null;
  meta: {
    [key: string]: any;
  };
}

export type ResponderResponse = PaginatedResponse<Responder>;

// responding
export interface Responding {
  id: number;
  companyId: number;
  groupId: number;
  responderId: number;
  status: GroupStatus;
  createdAt: string;
  updatedAt: string;
  company: Company;
  group: Group;
  responder: Responder;
  isAnswered?: boolean;
  answers?: Answer[];
}

export type RespondingResponse = PaginatedResponse<Responding>;

// organization responding
export interface OrganizationResponding {
  id: number;
  organizationId: number;
  responderId: number;
  status: GroupStatus;
  createdAt: string;
  updatedAt: string;
  organization: Organization;
  responder: Responder;
}

export type OrganizationRespondingResponse =
  PaginatedResponse<OrganizationResponding>;

// criteria
export interface Criteria {
  id: number;
  name: string;
  description: string;
  type: GroupType;
  status: GroupStatus;
  index: number;
}

export type GroupCriteriaPayload = {
  groupId: number;
  criteriaIds: string[];
};

// group
export enum GroupType {
  MICRO = "MICRO",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export enum GroupStatus {
  ACTIVE = "ACTIVE",
  DEACTIVE = "DEACTIVE",
}

export interface Group {
  id: number;
  name: string;
  description: string;
  type: GroupType;
  status: GroupStatus;
  criterias?: Criteria[];
  key: string;
}

export enum GroupRowStateType {
  EXISTING = "EXISTING",
  NEW = "NEW",
}

export type GroupRowState = {
  groupId: number;
  nationalCode: string;
  mobile: string;
  responderId?: number;
  responderName?: string;
  type: GroupRowStateType;
  searchStatus?: "idle" | "found";
  isSubmitting?: boolean;
};

// assessment
export enum AssessmentStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
}

export enum LifeCyclePosition {
  INFANCY = "INFANCY",
  GROWTH = "GROWTH",
  MATURITY = "MATURITY",
  EVOLUTION = "EVOLUTION",
  STABILITY = "STABILITY",
  EARLY_DECLINE = "EARLY_DECLINE",
  DECLINE = "DECLINE",
  DEATH = "DEATH",
}

export enum StrategyStability {
  Low = "Low",
  Medium = "Medium",
  High = "High",
  VeryHigh = "VeryHigh",
}

export interface ReportData {
  ohi: number | null;
  lifeCycle: {
    score: number | null;
    position: LifeCyclePosition | null;
  };
  strategy: {
    stability: StrategyStability | null;
    order: number | null;
  };
}

export interface Assessment {
  id: number;
  companyId: number;
  licenseId: number;
  organizationId: number;
  organization?: Organization;
  status: AssessmentStatus;
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  completedAt: string;
  company: Company;
  license: LicenseDTO;
  answers?: Answer[];
  quantitativeValues?: QuantitativeValueType[];
  kpiResult?: JSON;
  scoringResults?: AssessmentScoringResult[];
  updated: boolean;
  report?: ReportData;
  reports?: ReportData;
}

export type AssessmentResponse = PaginatedResponse<Assessment>;

// organization assessment
export interface OrganizationAssessment {
  id: number;
  responderId: number;
  companyId: number;
  organizationId: number;
  status: AssessmentStatus;
  createdAt: string;
  decisionStatus: OrganizationAssessmentStatus;
  companyOhi?: number;
  answers: {
    questionId: number;
    optionId: number;
    description: string;
    responder?: Responder;
    responding?: Responding;
    question?: FormQuestionType;
    option?: FormQuestionOption;
  }[];
  scores?: {
    average: number;
    createdAt: string;
    id: number;
    normalizeAverage: number;
    organizationAssessmentId: number;
    question: FormQuestionType;
    questionId: number;
    updatedAt: string;
  }[];
  category?: "A" | "B" | "C" | "D" | "E" | "F";
  completedAt?: string;
  finalScore?: number;
  company?: Company;
  responder?: Responder;
  organization?: Organization;
  responding?: OrganizationResponding;
  responders?: OrganizationResponding[];
  assessment?: Assessment;
}

export enum OrganizationAssessmentStatus {
  MAINTAIN = "MAINTAIN",
  CONDITIONAL = "CONDITIONAL",
  WARNING = "WARNING",
  OPPORTUNITY = "OPPORTUNITY",
  CONDITIONAL_IMPROVE = "CONDITIONAL_IMPROVE",
  SERIOUS_WARNING = "SERIOUS_WARNING",
  LIMITED_REVIVAL = "LIMITED_REVIVAL",
  SUSPENSION = "SUSPENSION",
  TERMINATION = "TERMINATION",
}

export type OrganizationAssessmentResponse =
  PaginatedResponse<OrganizationAssessment>;

// chapter
export interface Chapter {
  id: number;
  name: string;
}

// question
export enum UnitType {
  TON = "TON",
  PIECE = "PIECE",
  WORKING_HOUR = "WORKING_HOUR",
  SHIFT = "SHIFT",
  KILOGRAM = "KILOGRAM",
  METER = "METER",
  LITER = "LITER",
  MILLION = "MILLION",
  PERSON = "PERSON",
  NUMBER = "NUMBER",
  OTHER = "OTHER",
}

export enum QuestionType {
  QUANTITATIVE = "QUANTITATIVE",
  QUALITATIVE = "QUALITATIVE",
}

export enum OptionKey {
  STRONGLY_DISAGREE = "STRONGLY_DISAGREE",
  DISAGREE = "DISAGREE",
  NEUTRAL = "NEUTRAL",
  AGREE = "AGREE",
  STRONGLY_AGREE = "STRONGLY_AGREE",
}

export interface QuestionOption {
  id: number;
  title: string;
  value: number;
  order: number;
  meta: null | any;
  active: boolean;
  questionId: number;
  key: OptionKey;
}

export interface QuestionItem {
  id: number;
  text: string;
  type: QuestionType;
  order: number;
  criteria: Criteria;
  options: QuestionOption[];
  criteriaId: number;
  meta: boolean;
  createdAt: string;
  index: number;
  name?: string;
}

export type QuestionResponse = QuestionItem[];

// quantitativeValues
export interface QuantitativeValueType {
  assessmentId: number;
  createdAt: string;
  id: number;
  rawMetricCode: RawMetricCode;
  updatedAt: string;
  value: string;
  year: number;
  unit: string;
  assessment: Assessment;
}

export interface Kpi {
  code: string;
  weight: number;
  normalized: number;
}

export interface CriteriaBreakdown {
  kpis: Kpi[];
  likertScore: number;
  criteriaIndex: number;
  likertPercent: number;
  questionCount: number;
  criteriaNumber: number;
  correctedPercent: number;
  correctionFactor: number;
  objectivePercent: number;
  correctedLikertScore: number;
}

export interface KpiBreakdownRaw {
  code: string;
  meta: {
    jsKey: string;
    rawKey?: string;
    selectedYears: number[];
    note?: string;
  };
  rawValue: number;
  normalized: number;
}

export interface KpiBreakdown {
  raw: KpiBreakdownRaw[];
  source: string;
  hardScore: number;
}

export interface AssessmentScoringResult {
  id: number;
  assessmentId: number;
  softScore: number;
  hardScore: number;
  finalScore: number;
  correctionFactor: number;
  stage: string;
  criteriaBreakdown: CriteriaBreakdown[];
  kpiBreakdown: KpiBreakdown;
  questionBreakdown: any[];
  createdAt: string;
  assessment: Assessment;
}

// asnwer
export interface Answer {
  id: number;
  assessmentId: number;
  respondingId: number;
  questionId: number;
  optionId: number;
  value: number;
  percent: null | number;
  questionAverage: null | number;
  createdAt: string;
  updatedAt: string;
  question?: QuestionItem;
  option?: QuestionOption;
}

export interface AnswerRequest {
  assessmentId: number;
  respondingId: number;
  data: number[];
}

// organization asnwer
export interface OrganizationAnswer {
  id: number;
  assessmentId: number;
  respondingId: number;
  questionId: number;
  optionId: number;
  value: number;
  percent: null | number;
  questionAverage: null | number;
  createdAt: string;
  updatedAt: string;
  question?: QuestionItem;
  option?: QuestionOption;
}

export interface OrganizationAnswerRequest {
  organizationAssessmentId: number;
  organizationRespondingId: number;
  answers: {
    questionId: number;
    optionId: number;
    description: string;
  }[];
}

// license
export enum GatewayType {
  ZARINPAL = "ZARINPAL",
  STRIPE = "STRIPE",
  BITPAY = "BITPAY",
}

export enum LicenseScope {
  COMPANY = "COMPANY",
  ORGANIZATION = "ORGANIZATION",
}

export enum LicenseTier {
  NORMAL = "NORMAL",
  SPECIAL = "SPECIAL",
}

export interface PurchaseLicenseDto {
  scope: LicenseScope;
  tier: LicenseTier;
  quantity: number;
  gateway?: GatewayType;
  groupType?: GroupType;
  companyId?: number;
  organizationId?: number;
  userId?: number;
  email?: string;
  mobile?: string;
}

export interface PurchaseLicenseResponse {
  success?: boolean;
  amount: number;
  authority: string;
  redirectUrl: string;
  transactionId: number | string;
  message?: string;
}

export interface PlanDto {
  id: number;
  groupType?: GroupType;
  organizationId?: number;
  organization?: Organization | null;
  tier: LicenseTier;
  title?: string;
  price: number;
  isActive: boolean;
  meta?: {
    [key: string]: any;
  };
}

export interface LicenseDTO {
  id: number;
  licenseKey: string;
  createdAt: string;
  usedAt: string | null;
  used: boolean;
  company?: Partial<Company> | null;
  companyId?: number | null;
  organizationId?: number | null;
  organization?: Organization | null;
  groupType?: GroupType;
  userId?: number;
  user?: User | null;
  tier?: LicenseTier;
  title?: string;
  price?: number;
  isActive?: boolean;
  scope?: LicenseScope;
}

export type LicensePlanListResponse = PaginatedResponse<PlanDto>;
export type LicensesListResponse = PaginatedResponse<LicenseDTO>;

// transaction
export enum TransactionStatus {
  SUCCESS = "SUCCESS",
  FAILED = "FAILED",
  PENDING = "PENDING",
}

export interface TransactionDTO {
  id: number;
  amount: number;
  authority: string;
  createdAt: string;
  deletedAt: string | null;
  description: string;
  gateway: GatewayType;
  licenses: LicenseDTO[];
  organization: any | null;
  organizationId: number | null;
  quantity: number;
  refId: string;
  status: TransactionStatus;
  updatedAt: string;
  user: User;
  userId: number;
}

export type TransactionsListResponse = PaginatedResponse<TransactionDTO>;

// role criteria
export interface RuleCriteria {
  id: number;
  criteriaId: number;
  companyStage: CompanyStage | "";
  groupType: GroupType | "";
}

// chapter 4 form
export type RawMetricCode =
  | "NET_SALES"
  | "OPERATING_PROFIT"
  | "NET_PROFIT"
  | "TOTAL_ASSETS"
  | "NOMINAL_CAPACITY"
  | "ACTUAL_PRODUCTION"
  | "INVENTORY"
  | "ACCOUNTS_RECEIVABLE"
  | "ACCOUNTS_PAYABLE"
  | "LOGISTICS_COST"
  | "RETURN_VALUE"
  | "RND_COST"
  | "TOTAL_ENERGY_COST"
  | "RENEWABLE_ENERGY_COST"
  | "VOLUNTARY_EXIT_EMPLOYEES"
  | "AVG_EMPLOYEES"
  | "CURRENT_EMPLOYEES"
  | "ACTIVE_PATENTS"
  | "NEW_PATENT_REQUESTS"
  | "NET_SALES_KNOWLEDGE_AND_TECHNOLOGIC"
  | "EXPORT_AMOUNT"
  | "EXPORT_VOLUME";

export const RAW_METRIC_CODES: RawMetricCode[] = [
  "NET_SALES",
  "OPERATING_PROFIT",
  "NET_PROFIT",
  "TOTAL_ASSETS",
  "NOMINAL_CAPACITY",
  "ACTUAL_PRODUCTION",
  "INVENTORY",
  "ACCOUNTS_RECEIVABLE",
  "ACCOUNTS_PAYABLE",
  "LOGISTICS_COST",
  "RETURN_VALUE",
  "RND_COST",
  "TOTAL_ENERGY_COST",
  "RENEWABLE_ENERGY_COST",
  "VOLUNTARY_EXIT_EMPLOYEES",
  "AVG_EMPLOYEES",
  "CURRENT_EMPLOYEES",
  "ACTIVE_PATENTS",
  "NEW_PATENT_REQUESTS",
  "NET_SALES_KNOWLEDGE_AND_TECHNOLOGIC",
  "EXPORT_AMOUNT",
  "EXPORT_VOLUME",
];

export type StrategicKpiCode =
  | "REAL_REVENUE_GROWTH"
  | "OPERATING_MARGIN"
  | "CAPACITY_UTILIZATION"
  | "CCC"
  | "ROA"
  | "EMPLOYEE_TURNOVER"
  | "LOGISTICS_TO_SALES"
  | "RETURN_RATE"
  | "RND_TO_SALES"
  | "RENEWABLE_ENERGY_SHARE"
  | "IP_POWER"
  | "ORGANIZATION_SIZE";

export const STRATEGIC_KPI_CODES: StrategicKpiCode[] = [
  "REAL_REVENUE_GROWTH",
  "OPERATING_MARGIN",
  "CAPACITY_UTILIZATION",
  "CCC",
  "ROA",
  "EMPLOYEE_TURNOVER",
  "LOGISTICS_TO_SALES",
  "RETURN_RATE",
  "RND_TO_SALES",
  "RENEWABLE_ENERGY_SHARE",
  "IP_POWER",
  "ORGANIZATION_SIZE",
];

export interface QuantitativeValueItem {
  rawMetricCode: RawMetricCode;
  year: number;
  value: number;
  unit?: string;
}

export interface QuantitativeValuesPayload {
  assessmentId: number;
  values: QuantitativeValueItem[];
}

export interface QuantitativeFormData {
  [key: string]: {
    [year: number]: number | undefined;
    unit?: string;
  };
}

export interface MetricData {
  [year: number]: number | undefined;
  unit?: string;
}

export interface MetricLabel {
  code: RawMetricCode;
  label: string;
  unit: string;
}
